import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Card, Form, FormControl, FormGroup, FormLabel } from "react-bootstrap";
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl";
import Selector from "../../../components/selector";
import { NON_REFUNDABLE_PAYMENTS, PAYMENT_FORM_ACTIONS } from "../../witadmin/Common/constants";
import { OperationOption, TransactionDto } from "../index";
import { StyledCard, StyledSpinner } from "../styles";
import { ConfirmModal } from "./ConfirmModal";
import { PaymentFormContainer } from "./styles";
import { TransactionList } from "./TransactionList";

const createMessage = <T extends Record<string, Record<string, MessageDescriptor>>>(messages: T) =>
  messages;

const messages = createMessage({
  modal: {
    title: {
      id: "reservationDetail.payment.modal.title",
      defaultMessage: "Warning",
    },
    ok: {
      id: "reservationDetail.payment.modal.ok",
      defaultMessage: "Confirm",
    },
    cancel: {
      id: "reservationDetail.payment.modal.cancel",
      defaultMessage: "Cancel",
    },
    description: {
      id: "reservationDetail.payment.modal.description",
      defaultMessage: "Are you sure to do a refund?. Once done it cannot go back.",
    },
  },
  payment: {
    title: {
      id: "reservationDetail.payment.title",
      defaultMessage: "Make payment / refund of {reservationId}",
    },
    titleNotRefund: {
      id: "reservationDetail.payment.titleNotRefund",
      defaultMessage: "Make payment of {reservationId}",
    },
    transactionType: {
      id: "reservationDetail.payment.transactionType",
      defaultMessage: "Transaction type",
    },
    selectTransactionType: {
      id: "reservationDetail.payment.selectTransactionType",
      defaultMessage: "Select a transaction type",
    },
    amount: {
      id: "reservationDetail.payment.amount",
      defaultMessage: "Amount",
    },
    description: {
      id: "reservationDetail.payment.description",
      defaultMessage: "Description",
    },
    confirmTransaction: {
      id: "reservationDetail.payment.confirmTransaction",
      defaultMessage: "Confirm transaction",
    },
    required: {
      id: "reservationDetail.payment.required",
      defaultMessage: "Required",
    },
    transactionId: {
      id: "reservationDetail.payment.transactionId",
      defaultMessage: "Transaction ID",
    },
    processing: {
      id: "reservationDetail.payment.processing",
      defaultMessage: "Processing...",
    },
    transactionIdHelperText: {
      id: "reservationDetail.payment.transactionIdHelperText",
      defaultMessage: "Enter a charge or authorization transaction ID.",
    },
    chargeAmountHelperText: {
      id: "reservationDetail.payment.chargeAmountHelperText",
      defaultMessage: "The amount to be refunded may not exceed the available amount",
    },
    invalidRefundTransaction: {
      id: "reservationDetail.payment.invalidRefundTransaction",
      defaultMessage: "This transaction is not available to refund",
    },
    invalidChargeAmountHelperText: {
      id: "reservationDetail.payment.invalidChargeAmountHelperText",
      defaultMessage: "The amount should be {value} {currency} at maximum.",
    },
    refundChargeAmountAvailableHelperText: {
      id: "reservationDetail.payment.refundChargeAmountAvailableHelperText",
      defaultMessage: "The max available amount to refund is {availableRefundAmount} {currency}",
    },
  },
  errors: {
    getPaymentOrderError: {
      id: "reservationDetail.transactionList.getPaymentOrderError",
      defaultMessage: "Error on getting payment order",
    },
  },
});

export interface PaymentFormProps {
  reservationId: string;
  operations: OperationOption[];
  transactions: TransactionDto[];
  transactionAmount: number;
  setTransactionAmount: (value: number) => void;
  transactionDescription: string;
  transactionId?: string;
  operationSelected?: OperationOption;
  onChangeAmount: (amount: number) => void;
  onChangeDescription: (description: string) => void;
  onChangeTransactionId: (transactionId?: string) => void;
  onSelectOperation: (event: any) => void;
  onConfirmTransaction: () => void;
  onConfirmRefund: () => void;
  setIsProcessing: (props: boolean) => void;
  isProcessing: boolean;
  modalOpen: boolean;
  setModalOpen: (isOpen: boolean) => void;
  isAlertHidden: boolean;
  alertVariant: "success" | "danger";
  alertMessage?: MessageDescriptor;
  pendingAmount: number;
  totalAmount: number;
  isNewPayment?: string;
  isLoadingTransactions: boolean;
  resetForm: () => void;
  showOperationsForm: boolean;
  paymentProvider?: string;
}

export function PaymentForm(props: PaymentFormProps) {
  const intl = useIntl();
  const [isInvalidTransaction, setIsInvalidTransaction] = useState<boolean>(false);
  const [isInvalidAmount, setIsInvalidAmount] = useState<boolean>(false);

  const dontHaveTransactions = useMemo(() => props.transactions.length === 0, [props.transactions]);

  const firstTransactionIsNotOk = useMemo(() => {
    return (
      props.transactions
        .filter(it => it.status === "OK")
        .slice(-1)
        .pop()?.status !== "OK"
    );
  }, [props.transactions]);

  const isRefund = useMemo(() => props.operationSelected?.value === PAYMENT_FORM_ACTIONS.REFUND, [
    props.operationSelected?.value,
  ]);

  const transactionIdIsDefined = useMemo(
    () => !!props.transactionId && props.transactionId.length > 0,
    [props.transactionId]
  );

  const isInvalidForm = useMemo(() => isInvalidTransaction || isInvalidAmount, [
    isInvalidAmount,
    isInvalidTransaction,
  ]);

  const isDisabledForm = useMemo(() => {
    const isInvalidAmount = props.transactionAmount <= 0;
    const dontHaveDescription = props.transactionDescription.length === 0;
    const dontHaveTransactionOrderNumber = props.transactionId?.length === 0;
    return (
      isInvalidForm ||
      firstTransactionIsNotOk ||
      !props.operationSelected ||
      isInvalidAmount ||
      dontHaveTransactions ||
      (!isRefund && dontHaveDescription) ||
      (isRefund && dontHaveTransactionOrderNumber)
    );
  }, [
    props.transactionAmount,
    props.transactionDescription.length,
    props.transactionId?.length,
    props.operationSelected,
    isInvalidForm,
    dontHaveTransactions,
    isRefund,
    firstTransactionIsNotOk,
  ]);

  const transactionOrderNumber = useMemo(() => {
    return props.transactions.find(it => it.id === props.transactionId)?.orderNumber;
  }, [props.transactionId, props.transactions]);

  const availableTransactionRefundAmount = useMemo(() => {
    return props.transactions
      .filter(it => it.orderNumber === transactionOrderNumber)
      .filter(it => !["KO", "REFUSED", "REJECTED"].includes(it.status))
      .map(it => {
        return it.type === "REFUND" ? it.total * -1 : it.total;
      })
      .reduce((acc, currentValue) => acc + currentValue, 0);
  }, [props.transactions, transactionOrderNumber]);

  const isValidRefundTransaction = useCallback(
    (transactionId: string) => {
      const selectedTransaction = props.transactions.find(it => it.id === transactionId);
      return (
        selectedTransaction &&
        selectedTransaction?.type !== "REFUND" &&
        !["KO", "REFUSED", "REJECTED"].includes(selectedTransaction?.status)
      );
    },
    [props.transactions]
  );

  const handleAmountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value.replace(",", ".");
      const finalAmount = isNaN(parseFloat(inputValue)) ? 0 : parseFloat(inputValue);
      props.setTransactionAmount(finalAmount);
      setIsInvalidAmount(isRefund && finalAmount > availableTransactionRefundAmount);
      props.onChangeAmount(finalAmount);
    },
    [availableTransactionRefundAmount, isRefund, props]
  );

  const hasNonRefundableTransaction = useMemo(
    () => props.paymentProvider && NON_REFUNDABLE_PAYMENTS.includes(props.paymentProvider),
    [props.paymentProvider]
  );

  return (
    <StyledCard className="ml-4 mr-4 mb-4 mt-4">
      <Card.Body>
        {props.showOperationsForm && (
          <div>
            <div className="border-bottom pb-2 mb-3 text-[21px]">
              {intl.formatMessage(
                messages.payment[hasNonRefundableTransaction ? "titleNotRefund" : "title"],
                {
                  reservationId: props.reservationId,
                }
              )}
            </div>
            <div className="mb-4 w-25">
              <PaymentFormContainer noValidate>
                <FormGroup>
                  <FormLabel className="font-weight-bold">
                    <FormattedMessage {...messages.payment.transactionType} />
                  </FormLabel>
                  <Selector
                    options={props.operations}
                    value={props.operationSelected}
                    onChange={(event: any) => {
                      props.resetForm();
                      props.onSelectOperation(event);
                    }}
                    placeholder={<FormattedMessage {...messages.payment.selectTransactionType} />}
                    className="w-100"
                    isDisabled={dontHaveTransactions || firstTransactionIsNotOk}
                    required
                  />
                </FormGroup>
                {isRefund && (
                  <FormGroup controlId="validationOperationTransactionId">
                    <FormLabel className="font-weight-bold">
                      <FormattedMessage {...messages.payment.transactionId} />
                    </FormLabel>
                    <FormControl
                      className="rounded-right w-100"
                      type="text"
                      placeholder={intl.formatMessage(messages.payment.required)}
                      onChange={event => {
                        setIsInvalidTransaction(!isValidRefundTransaction(event.target.value));
                        props.onChangeTransactionId(event.target.value);
                      }}
                      value={props.transactionId}
                      isInvalid={isInvalidTransaction}
                      disabled={!props.operationSelected || dontHaveTransactions}
                      required={isRefund}
                    />
                    <Form.Control.Feedback type="invalid">
                      <FormattedMessage {...messages.payment.invalidRefundTransaction} />
                    </Form.Control.Feedback>
                    <Form.Text muted hidden={transactionIdIsDefined}>
                      <FormattedMessage {...messages.payment.transactionIdHelperText} />
                    </Form.Text>
                    <Form.Text muted hidden={isInvalidTransaction || !transactionIdIsDefined}>
                      <FormattedMessage
                        {...messages.payment.refundChargeAmountAvailableHelperText}
                        values={{
                          availableRefundAmount: availableTransactionRefundAmount,
                          currency: "EUR",
                        }}
                      />
                    </Form.Text>
                  </FormGroup>
                )}
                <FormGroup controlId="validationOperationAmount">
                  <FormLabel className="font-weight-bold">
                    <FormattedMessage {...messages.payment.amount} />
                  </FormLabel>
                  <FormControl
                    type="number"
                    className="rounded-right w-100"
                    onChange={handleAmountChange}
                    disabled={
                      isInvalidTransaction || !props.operationSelected || dontHaveTransactions
                    }
                    max={availableTransactionRefundAmount}
                    value={props.transactionAmount}
                    min={0}
                    step={0.5}
                    required
                    isInvalid={props.transactionAmount > 0 && isInvalidAmount}
                  />
                  <Form.Control.Feedback type="invalid" hidden={!isRefund}>
                    <FormattedMessage
                      {...messages.payment.invalidChargeAmountHelperText}
                      values={{ value: availableTransactionRefundAmount, currency: "EUR" }}
                    />
                  </Form.Control.Feedback>
                  <Form.Text muted hidden={!isRefund}>
                    <FormattedMessage {...messages.payment.chargeAmountHelperText} />
                  </Form.Text>
                </FormGroup>
                {!isRefund && (
                  <FormGroup>
                    <FormLabel className="font-weight-bold">
                      <FormattedMessage {...messages.payment.description} />
                    </FormLabel>
                    <FormControl
                      type="text"
                      className="w-100"
                      placeholder={intl.formatMessage(messages.payment.required)}
                      onChange={event => props.onChangeDescription(event.target.value)}
                      disabled={isInvalidForm || !props.operationSelected || dontHaveTransactions}
                      value={props.transactionDescription}
                      required
                    />
                  </FormGroup>
                )}
              </PaymentFormContainer>
              <ConfirmModal
                isOpen={props.modalOpen}
                title={intl.formatMessage(messages.modal.title)}
                onClose={() => props.setModalOpen(false)}
                isProcessing={props.isProcessing}
                labelOk={intl.formatMessage(messages.modal.ok)}
                labelCancel={intl.formatMessage(messages.modal.cancel)}
                onConfirm={props.onConfirmRefund}>
                <p>
                  <FormattedMessage {...messages.modal.description} />
                </p>
              </ConfirmModal>
            </div>
            <div className="mb-5">
              <Button
                className="btn btn-default"
                disabled={isDisabledForm || props.isProcessing}
                type="submit"
                onClick={props.onConfirmTransaction}>
                {props.isProcessing ? (
                  <>
                    <StyledSpinner className="fa fa-spinner fa-spin mr-2" />
                    <FormattedMessage {...messages.payment.processing} />
                  </>
                ) : (
                  <FormattedMessage {...messages.payment.confirmTransaction} />
                )}
              </Button>
              <Alert
                hidden={props.isAlertHidden}
                variant={props.alertVariant}
                className="mt-3 w-50">
                {props.alertMessage && <FormattedMessage {...props.alertMessage} />}
              </Alert>
            </div>
          </div>
        )}
        <TransactionList
          data={props.transactions}
          isLoading={props.isLoadingTransactions}
          paymentType={props.paymentProvider}
        />
      </Card.Body>
    </StyledCard>
  );
}
